/**
 * Wish List store
 *
 * Saved products live in localStorage, keyed by customer id. A theme cannot write
 * customer metafields on its own, so the list is per browser and does not follow a
 * customer to another device. Everything goes through window.PSBWishlist so the
 * storage layer can be swapped for a server-backed one later without touching the UI.
 *
 * The key includes the customer id on purpose: these are salon computers routinely
 * shared between accounts, and one browser-wide key would hand an account's saved
 * products to whoever logs in next.
 */
(function () {
	'use strict'

	var customerId = window.wishlistCustomerId || null
	var enabled = !!customerId
	var STORAGE_KEY = enabled ? 'psb:wishlist:' + customerId : 'psb:wishlist'
	var MAX_ITEMS = 250
	var SYNC_SELECTOR = '[data-wishlist-toggle], [data-wishlist-count]'
	var UPDATED_EVENT = 'wishlist:updated'

	// Used only when localStorage refuses a write (quota, or private-mode blocking),
	// so the list still behaves for the rest of this page view.
	var memoryFallback = null
	var usingFallback = false

	function isValidEntry(entry) {
		return !!entry && typeof entry.handle === 'string' && entry.handle !== ''
	}

	function readRaw() {
		if (usingFallback) return memoryFallback || []

		try {
			var raw = window.localStorage.getItem(STORAGE_KEY)
			if (!raw) return []

			var parsed = JSON.parse(raw)
			return Array.isArray(parsed) ? parsed.filter(isValidEntry) : []
		} catch (error) {
			return []
		}
	}

	function writeRaw(list) {
		try {
			window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
			return true
		} catch (error) {
			usingFallback = true
			memoryFallback = list
			return true
		}
	}

	function announce(action, handle) {
		store.syncUI()

		document.dispatchEvent(
			new CustomEvent(UPDATED_EVENT, {
				detail: {
					items: store.items(),
					action: action,
					handle: handle || null
				}
			})
		)
	}

	function applyToggle(button, saved) {
		var handle = button.getAttribute('data-product-handle')
		var isSaved = handle ? saved.indexOf(handle) !== -1 : false
		var label = isSaved
			? button.getAttribute('data-label-remove') || 'Remove from wish list'
			: button.getAttribute('data-label-add') || 'Add to wish list'

		button.classList.toggle('is-in-wishlist', isSaved)
		button.setAttribute('aria-pressed', isSaved ? 'true' : 'false')
		button.setAttribute('aria-label', label)
		button.setAttribute('title', label)

		var labelEl = button.querySelector('[data-wishlist-label]')
		if (labelEl) labelEl.textContent = label
	}

	var store = {
		enabled: enabled,

		items: function () {
			return enabled ? readRaw() : []
		},

		handles: function () {
			return store.items().map(function (entry) {
				return entry.handle
			})
		},

		count: function () {
			return store.items().length
		},

		has: function (handle) {
			if (!handle) return false

			return store.items().some(function (entry) {
				return entry.handle === handle
			})
		},

		add: function (item) {
			if (!enabled) return false

			var handle = item && item.handle
			if (!handle || store.has(handle)) return false

			var list = readRaw()
			list.unshift({
				handle: handle,
				id: item.id == null ? null : String(item.id),
				added_at: Date.now()
			})

			writeRaw(list.slice(0, MAX_ITEMS))
			announce('add', handle)
			return true
		},

		remove: function (handle) {
			if (!enabled || !handle) return false

			var list = readRaw()
			var next = list.filter(function (entry) {
				return entry.handle !== handle
			})

			if (next.length === list.length) return false

			writeRaw(next)
			announce('remove', handle)
			return true
		},

		toggle: function (item) {
			if (!enabled) return null

			var handle = item && item.handle
			if (!handle) return null

			if (store.has(handle)) {
				return store.remove(handle) ? 'removed' : null
			}

			return store.add(item) ? 'added' : null
		},

		clear: function () {
			if (!enabled) return

			writeRaw([])
			announce('clear', null)
		},

		/** Re-applies saved state to any toggles and counters inside root. */
		syncUI: function (root) {
			var scope = root || document
			var saved = store.handles()
			var i

			if (scope.nodeType === 1 && scope.matches && scope.matches('[data-wishlist-toggle]')) {
				applyToggle(scope, saved)
			}

			var toggles = scope.querySelectorAll('[data-wishlist-toggle]')
			for (i = 0; i < toggles.length; i++) applyToggle(toggles[i], saved)

			// '' rather than '0' so :empty collapses the badge, the way the theme's
			// own cart bubble already behaves.
			var total = saved.length
			var text = total > 0 ? String(total) : ''
			var counters = scope.querySelectorAll('[data-wishlist-count]')
			for (i = 0; i < counters.length; i++) counters[i].textContent = text
		}
	}

	window.PSBWishlist = store

	// Keep other tabs of the same account in step. Never writes back.
	window.addEventListener('storage', function (event) {
		if (event.key && event.key !== STORAGE_KEY) return

		store.syncUI()
		document.dispatchEvent(
			new CustomEvent(UPDATED_EVENT, {
				detail: { items: store.items(), action: 'sync', handle: null }
			})
		)
	})

	function onReady() {
		if (!enabled) return

		store.syncUI()

		// One delegated listener, so hearts cloned by slick or injected by AJAX
		// filtering work without any per-button binding.
		document.addEventListener('click', function (event) {
			var target = event.target
			if (!target || !target.closest) return

			var button = target.closest('[data-wishlist-toggle]')
			if (!button) return

			// Cards wrap their media in a link; the heart must not navigate.
			event.preventDefault()
			event.stopPropagation()

			store.toggle({
				handle: button.getAttribute('data-product-handle'),
				id: button.getAttribute('data-product-id')
			})
		})

		// AJAX filtering, the wish list page and slick's cloned slides all inject
		// markup after load, so re-sync whatever appears.
		var pending = false
		function scheduleSync() {
			if (pending) return
			pending = true

			window.requestAnimationFrame(function () {
				pending = false
				store.syncUI()
			})
		}

		var observer = new MutationObserver(function (mutations) {
			for (var i = 0; i < mutations.length; i++) {
				var added = mutations[i].addedNodes

				for (var j = 0; j < added.length; j++) {
					var node = added[j]
					if (node.nodeType !== 1) continue

					if (
						(node.matches && node.matches(SYNC_SELECTOR)) ||
						(node.querySelector && node.querySelector(SYNC_SELECTOR))
					) {
						scheduleSync()
						return
					}
				}
			}
		})

		observer.observe(document.body, { childList: true, subtree: true })
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', onReady)
	} else {
		onReady()
	}
})()
